import React from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  ArrowTopRightOnSquareIcon,
  ClipboardDocumentIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { adminAPI } from '../../services/api';
import { openResolvedExternalUrl } from '../../utils/externalWindow';
import { Invoice } from '../../types/invoice';
import {
  formatCurrencyCents,
  formatDateShort,
  normalizeInvoiceStatus,
} from '../../utils/invoiceFormatting';
import InvoiceStatusBadge from './InvoiceStatusBadge';

type RecordInvoice = Record<string, any>;

type RecordInvoicesPanelProps = {
  contextType: 'customer' | 'account' | 'reservation' | 'record';
  invoices: RecordInvoice[];
  loading?: boolean;
  title?: string;
  emptyTitle?: string;
  emptyDescription?: string;
  createLabel?: string;
  createDisabled?: boolean;
  onCreateInvoice: () => void;
  onOpenInvoice: (invoice: RecordInvoice) => void;
  onRefresh?: () => void | Promise<void>;
  secondaryAction?: React.ReactNode;
};

function invoiceNumber(invoice: RecordInvoice) {
  return invoice.invoice_number || invoice.invoiceNumber || 'Invoice';
}

function invoiceRecipient(invoice: RecordInvoice) {
  const details = invoice.customer_details || invoice.customerDetails || {};
  return (
    details.name ||
    details.company_name ||
    details.companyName ||
    details.email ||
    invoice.customer_name ||
    invoice.customerName ||
    invoice.customer_email ||
    invoice.customerEmail ||
    'Customer'
  );
}

function invoiceEmail(invoice: RecordInvoice) {
  const details = invoice.customer_details || invoice.customerDetails || {};
  return details.email || invoice.customer_email || invoice.customerEmail || null;
}

function invoiceLineage(invoice: RecordInvoice, contextType: RecordInvoicesPanelProps['contextType']) {
  if (invoice.reservation_id || invoice.reservationId) return 'Reservation';
  if (invoice.grouped_payment_request_id || invoice.groupedPaymentRequestId) return 'Grouped request';
  if (invoice.import_batch_id || invoice.importBatchId) return 'Import';
  if (invoice.account_id || invoice.accountId) return 'Account';
  if (invoice.metadata?.source) return String(invoice.metadata.source);
  return contextType === 'record' ? 'Invoice' : `${contextType.charAt(0).toUpperCase()}${contextType.slice(1)}`;
}

function invoiceTotal(invoice: RecordInvoice) {
  return formatCurrencyCents(Number(invoice.total || 0), invoice.currency || 'USD');
}

function invoicePaidDue(invoice: RecordInvoice) {
  const paid = Number(invoice.amount_paid ?? invoice.amountPaid ?? 0);
  const due = Number(invoice.amount_due ?? invoice.amountDue ?? Math.max(Number(invoice.total || 0) - paid, 0));
  return `${formatCurrencyCents(paid, invoice.currency || 'USD')} paid / ${formatCurrencyCents(due, invoice.currency || 'USD')} due`;
}

function invoiceIssued(invoice: RecordInvoice) {
  return formatDateShort(invoice.issued_at || invoice.issuedAt || invoice.created_at || invoice.createdAt);
}

function invoiceDue(invoice: RecordInvoice) {
  return formatDateShort(invoice.due_date || invoice.dueDate);
}

function publicUrl(invoice: RecordInvoice) {
  return invoice.payment_url || invoice.paymentUrl || invoice.public_url || invoice.publicUrl || null;
}

export default function RecordInvoicesPanel({
  contextType,
  invoices,
  loading = false,
  title = 'Invoices',
  emptyTitle = 'No invoices yet',
  emptyDescription = 'Create an invoice from this record so billing context, contacts, and lineage stay connected.',
  createLabel = 'Create Invoice',
  createDisabled = false,
  onCreateInvoice,
  onOpenInvoice,
  onRefresh,
  secondaryAction,
}: RecordInvoicesPanelProps) {
  const navigate = useNavigate();

  async function resolvePublicLink(invoice: RecordInvoice): Promise<string> {
    let url = publicUrl(invoice);
    if (!url && invoice.id) {
      const response = await adminAPI.getInvoicePublicLink(String(invoice.id));
      url = response?.data?.public_url || response?.data?.publicUrl || response?.data?.payment_url || response?.data?.paymentUrl;
      await onRefresh?.();
    }
    if (!url) {
      throw new Error('No public link is available for this invoice yet.');
    }
    return url;
  }

  async function handleCopyPublicLink(invoice: RecordInvoice) {
    try {
      const url = await resolvePublicLink(invoice);
      if (!url) {
        toast.error('No public link is available for this invoice yet.');
        return;
      }
      await navigator.clipboard.writeText(url);
      toast.success('Invoice link copied');
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to copy invoice link');
    }
  }

  async function handleOpenPublic(invoice: RecordInvoice) {
    try {
      await openResolvedExternalUrl(() => resolvePublicLink(invoice));
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to open invoice link');
    }
  }

  return (
    <section className="card p-4">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h4 className="text-sm font-semibold text-on-surface">{title}</h4>
          <p className="mt-1 text-sm text-on-surface-variant">
            Create, review, and collect invoices tied to this {contextType}.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {secondaryAction}
          <button type="button" className="btn-primary inline-flex items-center gap-1.5 text-sm" onClick={onCreateInvoice} disabled={createDisabled}>
            <PlusIcon className="h-4 w-4" />
            {createLabel}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, index) => (
            <div key={index} className="h-14 animate-pulse rounded-card bg-surface-container-low" />
          ))}
        </div>
      ) : invoices.length === 0 ? (
        <div className="rounded-card border border-dashed border-outline/60 bg-surface-container-low p-5 text-center">
          <p className="text-sm font-semibold text-on-surface">{emptyTitle}</p>
          <p className="mx-auto mt-1 max-w-md text-sm text-on-surface-variant">{emptyDescription}</p>
          <button type="button" className="btn-secondary mt-4 inline-flex items-center gap-1.5 text-sm" onClick={onCreateInvoice} disabled={createDisabled}>
            <PlusIcon className="h-4 w-4" />
            {createLabel}
          </button>
        </div>
      ) : (
        <div className="overflow-hidden rounded-card border border-outline/50">
          <div className="hidden grid-cols-[1.25fr_1.25fr_0.8fr_0.9fr_1fr_0.8fr] gap-3 border-b border-outline/50 bg-surface-container-low px-3 py-2 text-xs font-semibold uppercase tracking-wide text-on-surface-variant lg:grid">
            <span>Invoice</span>
            <span>Recipient</span>
            <span>Status</span>
            <span>Total</span>
            <span>Paid / due</span>
            <span className="text-right">Actions</span>
          </div>
          <div className="divide-y divide-outline/50">
            {invoices.map((invoice) => (
              <button
                key={invoice.id || invoiceNumber(invoice)}
                type="button"
                className="grid w-full gap-3 px-3 py-3 text-left transition-colors hover:bg-surface-container-low lg:grid-cols-[1.25fr_1.25fr_0.8fr_0.9fr_1fr_0.8fr] lg:items-center"
                onClick={() => onOpenInvoice(invoice)}
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-on-surface">{invoiceNumber(invoice)}</p>
                  <p className="mt-0.5 text-xs text-on-surface-variant">{invoiceLineage(invoice, contextType)}</p>
                </div>
                <div className="min-w-0">
                  <p className="truncate text-sm text-on-surface">{invoiceRecipient(invoice)}</p>
                  {invoiceEmail(invoice) ? (
                    <p className="truncate text-xs text-on-surface-variant">{invoiceEmail(invoice)}</p>
                  ) : null}
                </div>
                <div>
                  <InvoiceStatusBadge status={normalizeInvoiceStatus(invoice as Invoice)} />
                </div>
                <div className="text-sm font-semibold text-on-surface">
                  {invoiceTotal(invoice)}
                  <p className="mt-0.5 text-xs font-normal text-on-surface-variant">
                    Issued {invoiceIssued(invoice)} · Due {invoiceDue(invoice)}
                  </p>
                </div>
                <div className="text-sm text-on-surface-variant">{invoicePaidDue(invoice)}</div>
                <div className="flex flex-wrap justify-start gap-2 lg:justify-end" onClick={(event) => event.stopPropagation()}>
                  <button type="button" className="btn-secondary px-3 py-1.5 text-xs" onClick={() => onOpenInvoice(invoice)}>
                    Open
                  </button>
                  <button
                    type="button"
                    className="btn-secondary inline-flex items-center gap-1 px-3 py-1.5 text-xs"
                    onClick={() => navigate(`/admin/invoices?search=${encodeURIComponent(invoiceNumber(invoice))}`)}
                  >
                    <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                    List
                  </button>
                  <button
                    type="button"
                    className="btn-secondary inline-flex items-center gap-1 px-3 py-1.5 text-xs"
                    onClick={() => handleOpenPublic(invoice)}
                  >
                    <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                    Public
                  </button>
                  <button
                    type="button"
                    className="btn-secondary inline-flex items-center gap-1 px-3 py-1.5 text-xs"
                    onClick={() => void handleCopyPublicLink(invoice)}
                  >
                    <ClipboardDocumentIcon className="h-3.5 w-3.5" />
                    Copy
                  </button>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
